import type {CSSProperties} from 'react';
import {font, radius, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeRepXpLook = 'repxp-today' | 'repxp-claim' | 'repxp-shop';

/* RepXP panel TODAY pill (the tappable "TODAY +N" chip in the panel header that opens the XP breakdown): a capsule with 5px by 11px
   padding, no border, 12px heavy type in the mono role. The ink is the rep's tier color and the fill is that color at 10% alpha. Both
   are data, so the caller passes them as the color and background of its style, built where the tier color is known. The look never
   builds a color string.
   It keeps the default transparent target (wedgeSettingsDrawsHit): the pill is alone in its header row, 12px above the XP bar, which is
   not a control, so the 44px region overlaps no neighbor. */

/* RepXP Rewards card CLAIM pill (the amber capsule on a merch milestone the rep has unlocked and not yet claimed): a capsule with a 1px amber
   border and amber fill, the page ink, 7px by 14px padding and 10.5px bold mono type, uppercase, with 1px of tracking. It inherits the font
   of its row first, as the source does, then sets the mono family. It reads the exact legacy roles, so the shipped ink rules keep matching.
   The caller owns the label, the handler, the dim while a claim is in flight (the opacity) and the shadow: the inset white edge and the
   amber glow are one string the caller builds, so the look draws no color of its own.
   It draws no transparent target and no anchor (wedgeSettingsDrawsHit). The anchor makes the pill a stacking context (position and
   isolation), and a browser check showed that this turns the subpixel text of the pill into grayscale text: hundreds of pixels differ from
   legacy in each state with a claimable pill, against 0 to 239 for the same-side noise. With the region off the pill matches legacy.
   The pill is 65px by 30px and rows sit 49px apart, so a mouse or touch tap has the pill itself as target and no neighbor to hit by mistake.
   `repxp-shop` is the merch name link on the same row: a flex item that takes the free width (`flex: 1`, `min-width: 0`), an inline flex row with a
   5px gap, 13.5px semibold type in the page ink, no underline and clipped overflow, so a long item name ends in an ellipsis. The caller draws the
   underlined name and the trailing glyph. It draws no transparent target and no anchor either (the CSS rule names `repxp-claim` and `repxp-shop`
   only, so `repxp-today` keeps its target): the clipped overflow would cut the region off at the link box, and a region left on anchors to a far
   ancestor and covers the next rows (browser check). */
export const isWedgeRepXpLook = (look: string | undefined): look is WedgeRepXpLook =>
  look === 'repxp-today' || look === 'repxp-claim' || look === 'repxp-shop';

/** The Rewards card looks draw no transparent target; the panel TODAY pill keeps it. */
export const isWedgeRepXpNoHitLook = (look: string | undefined): boolean => look === 'repxp-claim' || look === 'repxp-shop';

const SHOP_FONT = 13.5, SHOP_GAP = 5;
const CLAIM_FONT = 10.5, CLAIM_PAD = '7px 14px', CLAIM_TRACK = 1;

export function wedgeRepXpStyle(look: WedgeRepXpLook): CSSProperties {
  if (look === 'repxp-today') return {
    borderRadius: radius.pill, padding: '5px 11px',
    fontSize: font.caption, fontWeight: weight.heavy, fontFamily: wedgeExactRole('mono'), border: 0, cursor: 'pointer',
  };
  if (look === 'repxp-shop') return {
    flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: SHOP_GAP, fontSize: SHOP_FONT, fontWeight: 600, color: wedgeExactRole('label'),
    textDecoration: 'none', overflow: 'hidden',
  };
  return {
    font: 'inherit', fontFamily: wedgeExactRole('mono'), fontSize: CLAIM_FONT, fontWeight: 700, letterSpacing: CLAIM_TRACK, textTransform: 'uppercase',
    color: wedgeExactRole('bg'), background: wedgeExactRole('amber'), border: `1px solid ${wedgeExactRole('amber')}`, borderRadius: radius.pill,
    padding: CLAIM_PAD, cursor: 'pointer',
  };
}
